import React, { useState, useMemo, useCallback } from 'react'
import { useDrop } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'
import { dragType as documentDragType } from 'components/Document/Document'
import userDragType from 'components/User/dragType'
import BoardCardComponent from './CardComponent'
import CardComponent from 'components/Card'
import {
  ToggleModal
} from 'components/Modal'
import { labelDragType } from '../Sidebar/Labels/Labels'
import QuickActions from './QuickActions'
import useDroppable from 'components/Sortable/useDroppable'
import { useDispatch, useSelector } from 'react-redux'
import requestUploadStart from '../sagas/requestUploadStart'
import requestAddDocumentToCard from '../sagas/requestAddDocumentToCard'
import requestAddLabelToCard from '../sagas/requestAddLabelToCard'
import requestAddUserToCard from '../sagas/requestAddUserToCard'
import useMergedRef from '@react-hook/merged-ref'
import { useBoardContext, useBoardPermission } from 'dataProviders/Board'

const RenderCard = React.forwardRef<any, any>((props, ref) => {
  const {
    connectDocumentDropTarget,
    connectUserDropTarget,
    connectLabelDropTarget,
    isDocumentOver,
    isUserOver,
    isLabelOver,

    id,

    boardContext,
    permission,
    listId,
    isBusy,

    requestAddDocumentToCard,
    requestAddUserToCard,
    requestUploadStart,

    cardComponent = CardComponent,
    sortable,

    ...cardProps
  } = props

  return (
    <BoardCardComponent
      {...cardProps}
      ref={ref}
      cardComponent={cardComponent}
      id={id}
      isBusy={isBusy}
    />
  )
})

const Card = React.forwardRef<any, any>((props, ref) => {
  const {
    id,

    quickActions = true,
    sortable = {
      isDragging: false,
      connectDropTarget: () => {},
      connectDragSource: () => {}
    },
  } = props
  const [quickActionsTabId, setQuickActionsTabId] = useState<string | undefined>(undefined)

  const [showQuickActions, setShowQuickActions] = useState(false)

  const dispatch = useDispatch()

  const boardContext = useBoardContext()
  const permission = useBoardPermission()

  const listId = useSelector(state => state['view-board'].cards[props.id]?.listId)
  const isBusy = useSelector(state => state['view-board'].cards[props.id]?.isBusy)
  const noAccess = useSelector(state => state['view-board'].cards[props.id]?.noAccess)

  const preventEventPropagation = useCallback((event) => {
    event.stopPropagation()
  }, [])

  const handleQuickActionsToggle = useCallback((toggle) => {
    setShowQuickActions(toggle)

    if (!toggle) {
      setQuickActionsTabId(undefined)
    }
  }, [id])

  const handleLabelDrop = useCallback(({ itemId }) => {
    dispatch(requestAddLabelToCard({
      id,
      labelId: itemId
    }))
  }, [id])

  const handleQuickActionsClose = useCallback(() => {
    setShowQuickActions(false)
    setQuickActionsTabId(undefined)
  }, [])

  const [{ isOver: isLabelOver }, labelDrop] = useDroppable({
    name: 'labels',
    dragType: labelDragType,
    disabled: !permission.member,
    onDrop: handleLabelDrop,
  })

  const [{ isDocumentOver }, documentDrop] = useDrop(useMemo(() => ({
    accept: [NativeTypes.FILE, documentDragType],
    canDrop: () => !isBusy && !noAccess && permission.member,
    drop: (item) => {
      const { files, url, documentSourceId } = item

      if (files) {
        var count = files.length

        if (count > 0) {
          for (var i = 0; i < count; i++) {
            dispatch(requestUploadStart({
              file: files[i],
              boardId: boardContext.id,
              cardId: id
            }))
          }
        }
      } else {
        dispatch(requestAddDocumentToCard({
          id,
          url,
          documentSourceId
        }))
      }
    },
    collect: (monitor) => ({
      isDocumentOver: monitor.isOver()
    })
  }), [id, boardContext.id, isBusy, noAccess, permission]))

  const [{ isUserOver }, userDrop] = useDrop(useMemo(() => ({
    accept: userDragType,
    canDrop: () => !isBusy && !noAccess && permission.member,
    drop: (item) => {
      const { id: userId } = item

      dispatch(requestAddUserToCard({
        id,
        userId
      }))
    },
    collect: (monitor) => ({
      isUserOver: monitor.isOver()
    })
  }), [id, isBusy, noAccess, permission]))

  const mergedRef = useMergedRef(
    labelDrop,
    documentDrop,
    userDrop,
    ref,
    ref => {
      const node = !isBusy && !noAccess ? ref : null

      sortable.connectDropTarget(node)
      sortable.connectDragSource(node)
    },
  )

  return quickActions ? (
    <ToggleModal
      backdrop
      attachment='top left'
      targetAttachment='top left'
      constraints={[
        {
          to: 'window',
          pin: true
        }
      ]}
      show={showQuickActions}
      attachClickHandler={false}
      onToggle={handleQuickActionsToggle}
    >
      <RenderCard
        linkName
        ref={mergedRef}
        onMouseDown={preventEventPropagation}
        isDragging={sortable.isDragging}
        isDraggableOver={isDocumentOver || isUserOver || isLabelOver}
        onStatusClick={() => {
          setQuickActionsTabId('status')
          setShowQuickActions(true)
        }}
        {...props}
      />
      <QuickActions
        card={
          <RenderCard
            {...props}
            hover={false}
            linkName={false}
            showChildren={false}
            menu={undefined}
          />
        }
        id={id}
        listId={listId}
        close={handleQuickActionsClose}
        onClick={preventEventPropagation}
        defaultSelectedTabId={quickActionsTabId}
      />
    </ToggleModal>
  ) : (
    <RenderCard
      linkName
      ref={mergedRef}
      onMouseDown={preventEventPropagation}
      isDragging={sortable.isDragging}
      {...props}
    />
  )
})

export default Card
